import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";

// Import brand logo SVG
import LogoSVG from './Logo_Rich_Blue.svg';

// Import generated realistic image assets
import FloatingBeansImg from './floating_beans.png';
import CoffeeBagAndCupImg from './coffee_bag_and_cup.png';

// Import Aromista template stock images and IL CAPO coffee packages
import AboutWorkerImg from './about_worker.jpg';
import PerksCupImg from './perks_cup.png';
import MiddleHandsImg from './middle_hands.jpg';
import ProductClassicoImg from './product_classico.jpg';
import ProductIntensoImg from './product_intenso.jpg';
import ProductDolceImg from './product_dolce.jpg';
import ProductOrigineImg from './product_origine.jpg';

// ── Brand translations ─────────────────────────────────────────
const t = {
  en: {
    dir: "ltr",
    nav: ["Home", "Our Story", "Products", "How to Brew", "News", "Contact"],
    heroBadge: "Discover the Artistry in Every Roast",
    heroH1a: "Crafting Every",
    heroH1b: "Bean",
    heroH1c: ", Brewing",
    heroH1d: "Every Dream",
    heroP: "Explore our artisanal coffee roasts, crafted for perfection. Discover unique flavors and brewing tips.",
    shopNow: "Shop Now",
    learnMore: "Learn More",
    statsLabel1: "Happy Customers", statsDesc1: "Cafes, restaurants, and retail consumers receive our premium supplies.",
    statsLabel2: "Coffee Varieties", statsDesc2: "Blends and single-origin espressos roasted in authentic Italian style.",
    statsLabel3: "Bags Roasted", statsDesc3: "Every small-batch roast carefully crafted to perfection.",
    aboutLabel: "About Us",
    aboutTitle: "Crafting Perfection in Our Journey",
    aboutP1: "IL CAPO is a coffee brand inspired by the spirit of Italy — specifically from the Neapolitan roasting school known for its strength, depth, and rich cream.",
    aboutP2: "The coffee is carefully roasted in Libya with Italian expertise and techniques, to provide an authentic espresso experience that combines Italian heritage with Libyan craftsmanship.",
    perksLabel: "Discover More Delights",
    perksTitle: "Perks of Our Beans",
    perk1T: "Freshly Roasted", perk1P: "Our beans are freshly roasted to preserve flavor and aroma in every bag we deliver.",
    perk2T: "Roasting Mastery", perk2P: "Rich flavors unleashed through our Italian Neapolitan roasting tradition.",
    perk3T: "Crafted Consistency", perk3P: "Italian quality control ensures consistency and excellence in every single batch.",
    whyLabel: "Why Choose Us",
    whyTitle: "Why IL CAPO Sets the Standard",
    whyP: "It's not just coffee — it's a strong personality, a confident taste, and an experience designed for true espresso lovers.",
    w1T: "Exceptional Quality", w1P: "We source the finest espresso beans roasted with Italian expertise, from farm to cup.",
    w2T: "Artisanal Roasting", w2P: "Our master roasters follow the Neapolitan tradition, perfecting every roast.",
    w3T: "Authenticity & Pride", w3P: "Born from Italian heritage, proudly roasted and packed in Libya.",
    w4T: "Satisfaction Guarantee", w4P: "If you're not delighted, we go above and beyond to make it right.",
    productsLabel: "Products",
    productsTitle: "Our Popular Blends",
    orderNow: "Order Now",
    products: [
      { name: "Classico", origin: "Italian Blend", desc: "rich & bold, smooth finish", price: "$22" },
      { name: "Intenso", origin: "Dark Roast", desc: "dark roast, intense & velvety", price: "$22" },
      { name: "Dolce", origin: "Medium Roast", desc: "sweet & creamy, low acidity", price: "$20" },
      { name: "Origine", origin: "Single Origin", desc: "bright notes, floral & fruity", price: "$26" },
    ],
    testLabel: "Testimonial",
    testTitle: "What Our Happy Customers Say",
    testQuote: "IL CAPO Espresso has completely transformed my morning ritual. The depth of flavor in every cup is simply unmatched — you can taste the Italian craftsmanship in every single sip.",
    testName: "Francisco Javier",
    testRole: "Barcelona Brewses Owner",
    blogLabel: "Get To Know Us",
    blogTitle: "Sip into Our Blog for Brew-tiful Insights",
    blogs: [
      { date: "January 18, 2024", title: "Savoring Excellence: A Guide to Buying Espresso Beans Like a Connoisseur", excerpt: "As coffee culture continues to evolve, the discerning consumer seeks more than just a morning boost..." },
      { date: "January 18, 2024", title: "The Art and Science of Roastery: A Journey into Italian Flavor", excerpt: "In the bustling world of espresso culture, the roastery stands as the alchemist's workshop..." },
      { date: "January 3, 2024", title: "Celebrating the Stories of Coffee Farmers Around the World", excerpt: "Behind every exceptional espresso is a dedicated farmer whose story deserves to be told..." },
    ],
    readMore: "Read More →",
    newsletterTag: "Stay Updated",
    newsletterTitle: '"Caffeine Chronicles: Your Monthly Brew of Updates & Insights"',
    emailPlaceholder: "Enter your email address",
    subscribe: "Subscribe",
    footerAbout: "About", footerExplore: "Explore",
    footerAboutLinks: ["Contact Us", "Our Story", "News", "FAQs"],
    footerExploreLinks: ["How To Brew", "What's On", "Products", "Crew"],
    footerInquiries: "Inquiries",
    footerDesc: "Crafting exceptional Italian espresso experiences. Born in Italy, proudly roasted in Libya.",
    footerCopy: "© 2026 IL CAPO Espresso. All Rights Reserved.",
    missionLabel: "Our Mission", missionText: "Providing high-quality espresso coffee, roasted in an authentic Italian style, that meets the requirements of cafés and professionals.",
    visionLabel: "Our Vision", visionText: "For IL CAPO to be a leading espresso brand in Libya and the region, with a strong presence in Arab markets.",
    valuesLabel: "Our Values", values: ["Authenticity", "Quality", "Craftsmanship", "Consistency", "Pride of Origin"],
  },
  ar: {
    dir: "rtl",
    nav: ["الرئيسية", "قصتنا", "المنتجات", "طريقة التحضير", "الأخبار", "اتصل بنا"],
    heroBadge: "اكتشف الفن في كل تحميص",
    heroH1a: "نصنع كل",
    heroH1b: "حبة",
    heroH1c: "، ونُحضّر",
    heroH1d: "كل حلم",
    heroP: "استكشف تحميصاتنا الحرفية من الإسبريسو، المُصنَّعة بعناية في التقليد الإيطالي الأصيل. اكتشف نكهات فريدة ونصائح التحضير.",
    shopNow: "تسوّق الآن",
    learnMore: "اعرف أكثر",
    statsLabel1: "عميل سعيد", statsDesc1: "المقاهي والمطاعم والمستهلكون يحصلون على إمداداتنا العالية الجودة.",
    statsLabel2: "نوع قهوة", statsDesc2: "خلطات وإسبريسو أحادي المصدر محمَّص بالأسلوب الإيطالي الأصيل.",
    statsLabel3: "كيس محمَّص", statsDesc3: "كل تحميص صغير الدُّفعة مُصنَّع بعناية تامة.",
    aboutLabel: "من نحن",
    aboutTitle: "نصنع الكمال في رحلتنا",
    aboutP1: "إل كابو هو علامة تجارية للقهوة مستوحاة من روح إيطاليا — وبالتحديد من مدرسة التحميص النابوليتانية المعروفة بالقوة والعمق والكريما الغنية.",
    aboutP2: "يتم تحميص القهوة بعناية في ليبيا بخبرات وتقنيات إيطالية، لتقديم تجربة إسبريسو أصيلة تجمع بين التراث الإيطالي والحرفية الليبية.",
    perksLabel: "اكتشف المزيد",
    perksTitle: "مميزات حبوبنا",
    perk1T: "تحميص طازج", perk1P: "تُحمَّص حبوبنا طازجةً للحفاظ على النكهة والأريج في كل كيس نووصله.",
    perk2T: "إتقان التحميص", perk2P: "نكهات غنية تنبثق عبر تقليد التحميص النابوليتاني الإيطالي الأصيل.",
    perk3T: "الاتساق الحرفي", perk3P: "رقابة الجودة الإيطالية تضمن الاتساق والتميز في كل دُفعة.",
    whyLabel: "لماذا نحن",
    whyTitle: "لماذا إل كابو يضع المعيار",
    whyP: "ليست مجرد قهوة — بل شخصية قوية، وذوق واثق، وتجربة صُمِّمت لعشّاق الإسبريسو الحقيقيين.",
    w1T: "جودة استثنائية", w1P: "نختار أفضل حبوب الإسبريسو المحمَّصة بخبرة إيطالية، من المزرعة إلى الكوب.",
    w2T: "تحميص حرفي", w2P: "يتبع محمِّصونا الرئيسيون التقليد النابوليتاني لإتقان كل تحميص.",
    w3T: "الأصالة والفخر", w3P: "وُلد من التراث الإيطالي، ويُحمَّص ويُعبَّأ بفخر في ليبيا.",
    w4T: "ضمان الرضا", w4P: "إذا لم تكن راضياً، فسنبذل كل جهد لنجعل الأمر صحيحاً.",
    productsLabel: "المنتجات",
    productsTitle: "مزيجاتنا الأكثر شعبية",
    orderNow: "اطلب الآن",
    products: [
      { name: "كلاسيكو", origin: "خلطة إيطالية", desc: "غني وجريء، نهاية ناعمة", price: "22$" },
      { name: "إنتنسو", origin: "تحميص داكن", desc: "تحميص داكن، مكثف وحريري", price: "22$" },
      { name: "دولتشي", origin: "تحميص متوسط", desc: "حلو وكريمي، حموضة منخفضة", price: "20$" },
      { name: "أوريجيني", origin: "أحادي المصدر", desc: "نكهات زاهية، زهري وفاكهي", price: "26$" },
    ],
    testLabel: "آراء العملاء",
    testTitle: "ماذا يقول عملاؤنا السعداء",
    testQuote: "غيَّر إسبريسو إل كابو طقوسي الصباحية تماماً. عمق النكهة في كل كوب لا مثيل له — تستطيع أن تتذوق الحرفية الإيطالية في كل رشفة.",
    testName: "فرانشيسكو خافيير",
    testRole: "مالك مقهى برشلونة",
    blogLabel: "تعرف علينا",
    blogTitle: "تعمق في مدونتنا للاطلاع على أحدث الرؤى",
    blogs: [
      { date: "١٨ يناير ٢٠٢٤", title: "الاستمتاع بالتميز: دليل شراء حبوب الإسبريسو كخبير", excerpt: "مع تطور ثقافة القهوة، يبحث المستهلك المميز عن أكثر من مجرد جرعة صباحية..." },
      { date: "١٨ يناير ٢٠٢٤", title: "فن وعلم المحمصة: رحلة إلى قلب النكهة الإيطالية", excerpt: "في عالم ثقافة الإسبريسو الصاخب، تقف المحمصة كورشة عمل الكيميائي..." },
      { date: "٣ يناير ٢٠٢٤", title: "الاحتفاء بقصص مزارعي القهوة حول العالم", excerpt: "خلف كل كوب إسبريسو استثنائي مزارع متفانٍ تستحق قصته أن تُروى..." },
    ],
    readMore: "اقرأ المزيد ←",
    newsletterTag: "ابقَ على اطلاع",
    newsletterTitle: '"سجلات الكافيين: جرعتك الشهرية من التحديثات والرؤى"',
    emailPlaceholder: "أدخل بريدك الإلكتروني",
    subscribe: "اشترك",
    footerAbout: "من نحن", footerExplore: "استكشف",
    footerAboutLinks: ["اتصل بنا", "قصتنا", "الأخبار", "الأسئلة الشائعة"],
    footerExploreLinks: ["طريقة التحضير", "الفعاليات", "المنتجات", "فريقنا"],
    footerInquiries: "للاستفسار",
    footerDesc: "نصنع تجارب إسبريسو إيطالية استثنائية. وُلدنا في إيطاليا، ونُحمَّص بفخر في ليبيا.",
    footerCopy: "© 2026 إل كابو إسبريسو. جميع الحقوق محفوظة.",
    missionLabel: "مهمتنا", missionText: "تقديم قهوة إسبريسو عالية الجودة، محمَّصة بأسلوب إيطالي أصيل، تلبي متطلبات المقاهي والمحترفين.",
    visionLabel: "رؤيتنا", visionText: "أن تكون إل كابو علامة إسبريسو رائدة في ليبيا والمنطقة، مع حضور قوي في الأسواق العربية.",
    valuesLabel: "قيمنا", values: ["الأصالة", "الجودة", "الحرفية", "الثبات", "الاعتزاز بالمصدر"],
  },
};

// ── Color tokens (Aromista Design System) ─────────────────────
const RICH_BLUE      = "#2C1E18"; // Deep Espresso
const ALABASTER      = "#FAF7F2"; // Warm Beige
const HIGHLIGHT_BLUE = "#C5A070"; // Gold Highlight
const LIGHT_BLUE     = "#D8C2A8"; // Soft Gold
const GOLD           = "#C5A070"; // Primary Gold

// ── Font helpers ─────────────────────────────────────────────
const FONT_BODY_EN = "'Inter', 'Segoe UI', sans-serif";
const FONT_HEADING_EN = "'Playfair Display', Georgia, serif";
const FONT_AR      = "'Cairo', 'Segoe UI', sans-serif";

// ── IL CAPO Brand Logo Component ──────────────────────────────
function IlCapoLogo({ size = 52, variant = "blue-on-alabaster", style = {} }) {
  const isLight = variant === "alabaster-on-blue";
  return (
    <img
      src={LogoSVG}
      alt="IL CAPO Espresso"
      style={{
        display: "block",
        width: "100%",
        maxWidth: size,
        height: "auto",
        objectFit: "contain",
        // Invert to white on dark backgrounds
        filter: isLight ? "brightness(0) invert(1)" : "none",
        ...style,
      }}
    />
  );
}

// ── Search Icon component ─────────────────────────────────────
function SearchIcon({ color = "#fff", size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: "pointer", transition: "opacity 0.2s" }} onMouseEnter={(e) => e.target.style.opacity = 0.8} onMouseLeave={(e) => e.target.style.opacity = 1}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// ── Hamburger Menu Icon component ──────────────────────────────
function HamburgerIcon({ color = "#fff", size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: "pointer", transition: "opacity 0.2s" }} onMouseEnter={(e) => e.target.style.opacity = 0.8} onMouseLeave={(e) => e.target.style.opacity = 1}>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}



// ── Main Website Component ────────────────────────────────────
export default function IlCapoWebsite() {
  const [lang, setLang] = useState(() => {
    const hash = window.location.hash;
    if (hash === "#/ar") return "ar";
    if (hash === "#/en") return "en";
    return "ar"; // Default to Arabic
  });

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === "#/ar") {
        setLang("ar");
      } else if (hash === "#/en") {
        setLang("en");
      } else {
        setLang("ar");
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const selectLanguage = (selectedLang) => {
    window.location.hash = `#/${selectedLang}`;
    setLang(selectedLang);
  };

  const tx    = t[lang];
  const isRTL = lang === "ar";

  const fontBody = isRTL ? FONT_AR : FONT_BODY_EN;
  const fontHeading = isRTL ? FONT_AR : FONT_HEADING_EN;


  const brandLogos = [
    // Logo 1: globe logo ipsum
    <div className="marquee-item">
      <span style={{ fontWeight: 500, fontSize: "1.15rem", letterSpacing: "-0.5px" }}>logo</span>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 2px" }}>
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
      <span style={{ fontWeight: 800, fontSize: "1.15rem", letterSpacing: "-0.5px" }}>ipsum</span>
    </div>,
    // Logo 2: dots logoipsum
    <div className="marquee-item">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" r="3" fill="currentColor" />
        <circle cx="15" cy="8" r="3" fill="currentColor" />
        <circle cx="9" cy="16" r="3" fill="currentColor" />
        <circle cx="18" cy="15" r="3" fill="currentColor" />
      </svg>
      <span style={{ fontWeight: 800, fontSize: "1.15rem", letterSpacing: "-0.5px" }}>logoipsum</span>
    </div>,
    // Logo 3: slanted capsule logoipsum
    <div className="marquee-item">
      <svg width="24" height="18" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 20 L8 4 L28 4 L24 20 Z" fill="currentColor" />
        <path d="M14 6 L10 18 L16 18 L13 22" stroke={ALABASTER} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span style={{ fontWeight: 800, fontSize: "1.15rem", letterSpacing: "-0.5px" }}>logoipsum</span>
    </div>,
    // Logo 4: waves logoipsum
    <div className="marquee-item">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 10 c3 -3, 6 -3, 9 0 c3 3, 6 3, 9 0" />
        <path d="M2 15 c3 -3, 6 -3, 9 0 c3 3, 6 3, 9 0" />
      </svg>
      <span style={{ fontWeight: 800, fontSize: "1.15rem", letterSpacing: "-0.5px" }}>logoipsum</span>
    </div>,
    // Logo 5: star logoipsum
    <div className="marquee-item">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2, 15.09 8.26, 22 9.27, 17 14.14, 18.18 21.02, 12 17.77, 5.82 21.02, 7 14.14, 2 9.27, 8.91 8.26, 12 2" />
      </svg>
      <span style={{ fontWeight: 800, fontSize: "1.15rem", letterSpacing: "-0.5px" }}>logoipsum</span>
    </div>
  ];

  return (
    <div style={{ fontFamily: fontBody, direction: tx.dir, background: ALABASTER, color: RICH_BLUE, overflowX: "hidden" }}>

      {/* ── SPLIT OVERLAY NAVBAR ── */}
      <nav style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1.8rem 5%",
        zIndex: 100,
        background: "transparent",
        direction: "ltr", // Force LTR layout so it aligns with the split background
      }}>
        {/* Logo container */}
        <div style={{ flex: "0 0 60%", display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
          <IlCapoLogo size={120} variant="blue-on-alabaster" />
        </div>

        {/* Icons container */}
        <div style={{
          flex: "0 0 40%",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "1.5rem",
        }}>
          <button
            onClick={() => selectLanguage(lang === "en" ? "ar" : "en")}
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              fontFamily: fontBody,
              fontSize: "0.85rem",
              fontWeight: 600,
              cursor: "pointer",
              opacity: 0.85,
              transition: "opacity 0.2s",
              marginInlineEnd: "0.5rem",
            }}
            onMouseEnter={(e) => e.target.style.opacity = 1}
            onMouseLeave={(e) => e.target.style.opacity = 0.85}
          >
            {lang === "en" ? "العربية" : "English"}
          </button>
          <SearchIcon color="#fff" size={24} />
          <HamburgerIcon color="#fff" size={24} />
        </div>
      </nav>

      {/* ── SPLIT HERO ── */}
      <section className="responsive-hero-grid" style={{
        position: "relative",
        overflow: "hidden",
        background: ALABASTER,
        direction: "ltr" // Force layout direction so text is always on the left, dark beans always on the right
      }}>
        {/* Left side: Cream content panel with text on the left and image on the right */}
        <div className="hero-cream-panel">
          {/* Text content container */}
          <div className="hero-text-container" style={{
            textAlign: isRTL ? "right" : "left",
            alignItems: isRTL ? "flex-end" : "flex-start",
            direction: isRTL ? "rtl" : "ltr" // Flow punctuation correctly in Arabic
          }}>
            <p style={{
              fontFamily: fontBody,
              fontSize: "0.82rem",
              color: HIGHLIGHT_BLUE,
              fontWeight: 700,
              marginBottom: "1.2rem",
              letterSpacing: 0.5,
              textTransform: "uppercase"
            }}>
              {tx.heroBadge}
            </p>
            <h1 style={{
              fontFamily: fontHeading,
              fontSize: "clamp(2.2rem, 4.4vw, 3.4rem)",
              lineHeight: 1.15,
              color: RICH_BLUE,
              marginBottom: "1.8rem",
              fontWeight: 700,
              letterSpacing: "-0.5px",
            }}>
              {tx.heroH1a}{" "}
              <span style={{
                backgroundColor: HIGHLIGHT_BLUE,
                color: ALABASTER,
                padding: "0.15em 0.5em",
                borderRadius: "8px",
                display: "inline-block",
              }}>
                {tx.heroH1b}
              </span>
              {tx.heroH1c}
              <br />{tx.heroH1d}
            </h1>
            <p style={{
              fontFamily: fontBody,
              fontSize: "0.95rem",
              color: RICH_BLUE,
              lineHeight: 1.8,
              marginBottom: "2.5rem",
              maxWidth: 425,
              opacity: 0.85,
              fontWeight: 500
            }}>
              {tx.heroP}
            </p>
            
            <button className="btn-hover" style={{
              background: GOLD,
              color: "#fff",
              border: "none",
              padding: "0.85rem 2rem",
              borderRadius: "30px",
              fontFamily: fontBody,
              fontSize: "0.9rem",
              fontWeight: 700,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              boxShadow: "0 4px 10px rgba(197, 160, 112, 0.2)"
            }}>
              {tx.shopNow}
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isRTL ? "rotate(180deg)" : "none" }}>
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </span>
            </button>
          </div>

          {/* Coffee bag and cup transparent image container */}
          <div className="hero-image-container">
            <img src={CoffeeBagAndCupImg} alt="IL CAPO Coffee Bag and Saucer Cup" />
          </div>
        </div>

        {/* Right side: Dark container with beans background */}
        <div style={{
          background: RICH_BLUE,
          borderBottomLeftRadius: "120px",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          padding: "8rem 2rem 5rem",
        }}>
          <img
            src={FloatingBeansImg}
            alt="Floating Coffee Beans"
            className="floating-beans"
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              top: 0,
              left: 0,
              objectFit: "cover",
              pointerEvents: "none",
              opacity: 0.95
            }}
          />
        </div>
      </section>

      {/* ── BRAND LOGOS STRIP ── */}
      <div className="marquee-container">
        <div className="marquee-content">
          {brandLogos.map((logo, index) => React.cloneElement(logo, { key: `logo-1-${index}` }))}
          {brandLogos.map((logo, index) => React.cloneElement(logo, { key: `logo-2-${index}` }))}
          {brandLogos.map((logo, index) => React.cloneElement(logo, { key: `logo-3-${index}` }))}
        </div>
      </div>

      {/* ── STATS ── */}
      <div style={{ background: ALABASTER, padding: "2rem 5% 5rem" }}>
        <div style={{
          background: RICH_BLUE,
          borderRadius: "40px",
          padding: "3.5rem 2rem",
          maxWidth: "1100px",
          margin: "0 auto",
          boxShadow: "0 20px 40px rgba(31, 21, 17, 0.15)",
        }}>
          <div className="responsive-grid-stats">
            {[
              { num: "250+", label: tx.statsLabel1, desc: tx.statsDesc1 },
              { num: "100+", label: tx.statsLabel2, desc: tx.statsDesc2 },
              { num: "950+", label: tx.statsLabel3, desc: tx.statsDesc3 },
            ].map((s, i) => (
              <div key={i} style={{ 
                padding: "2rem 5%", 
                textAlign: "center", 
                borderInlineEnd: i < 2 ? "1px solid rgba(250, 247, 242, 0.12)" : "none",
                position: "relative"
              }}>
                <div style={{ display: "inline-flex", alignItems: "flex-start", justifyContent: "center" }}>
                  <span style={{ 
                    fontFamily: fontBody, 
                    fontSize: "3.5rem", 
                    color: ALABASTER, 
                    fontWeight: 700, 
                    lineHeight: 1 
                  }}>
                    {s.num}
                  </span>
                  {/* Custom Coffee Bean SVG Icon as superscript */}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginInlineStart: "2px", marginTop: "-4px" }}>
                    <g transform="rotate(35 12 12)">
                      <ellipse cx="12" cy="12" rx="9" ry="6" fill={GOLD} />
                      <path d="M4 12 C 8 10, 10 14, 12 12 C 14 10, 16 14, 20 12" stroke={RICH_BLUE} strokeWidth="1.8" strokeLinecap="round" fill="none" />
                    </g>
                  </svg>
                </div>
                <span style={{ 
                  fontFamily: fontBody, 
                  fontSize: "0.72rem", 
                  letterSpacing: 2, 
                  textTransform: "uppercase", 
                  color: GOLD, 
                  fontWeight: 700, 
                  display: "block", 
                  marginTop: "0.8rem", 
                  marginBottom: "0.5rem" 
                }}>
                  {s.label}
                </span>
                <p style={{ 
                  fontFamily: fontBody, 
                  fontSize: "0.82rem", 
                  color: ALABASTER, 
                  opacity: 0.75, 
                  lineHeight: 1.6, 
                  maxWidth: 220, 
                  margin: "0 auto", 
                  fontWeight: 500 
                }}>
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── ABOUT ── */}
      <section className="responsive-grid-split" style={{ background: ALABASTER, direction: isRTL ? "rtl" : "ltr" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "4rem 5%", minHeight: 480 }}>
          <div className="about-image-wrapper">
            <img src={AboutWorkerImg} alt="IL CAPO Barista Worker" className="about-worker-image" />
          </div>
        </div>
        <div style={{ padding: "5rem 8%", display: "flex", flexDirection: "column", justifyContent: "center", textAlign: isRTL ? "right" : "left" }}>
          <p style={{ fontFamily: fontBody, fontSize: "0.72rem", letterSpacing: 3, textTransform: "uppercase", color: HIGHLIGHT_BLUE, marginBottom: "0.8rem", fontWeight: 700 }}>{tx.aboutLabel}</p>
          <h2 style={{ fontFamily: fontHeading, fontSize: "clamp(1.9rem,3vw,2.8rem)", color: RICH_BLUE, lineHeight: 1.2, marginBottom: "1.5rem", fontWeight: 700 }}>{tx.aboutTitle}</h2>
          <p style={{ fontFamily: fontBody, fontSize: "0.92rem", color: RICH_BLUE, lineHeight: 1.85, marginBottom: "1rem", opacity: 0.8, fontWeight: 500 }}>{tx.aboutP1}</p>
          <p style={{ fontFamily: fontBody, fontSize: "0.92rem", color: RICH_BLUE, lineHeight: 1.85, marginBottom: "2rem", opacity: 0.8, fontWeight: 500 }}>{tx.aboutP2}</p>
          <button className="btn-hover" style={{
            background: RICH_BLUE, color: ALABASTER, border: "none",
            padding: "0.8rem 2rem", borderRadius: "30px",
            fontFamily: fontBody, fontSize: "0.82rem", fontWeight: 700,
            cursor: "pointer", alignSelf: isRTL ? "flex-end" : "flex-start", letterSpacing: 1, textTransform: "uppercase",
          }}>{tx.learnMore}</button>
        </div>
      </section>

      {/* ── MISSION / VISION / VALUES ── */}
      <section style={{ background: RICH_BLUE, padding: "5rem 5%" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "2px", background: "rgba(247,245,235,0.08)" }}>
          {[
            { label: tx.missionLabel, text: tx.missionText, icon: "🎯" },
            { label: tx.visionLabel,  text: tx.visionText,  icon: "💡" },
            { label: tx.valuesLabel,  text: null,            icon: "💎" },
          ].map((item, i) => (
            <div key={i} style={{ background: RICH_BLUE, padding: "3rem 2rem", textAlign: "center" }}>
              <div style={{
                display: "inline-block", background: HIGHLIGHT_BLUE, color: ALABASTER,
                padding: "0.5rem 1.4rem", borderRadius: "4px",
                fontFamily: fontBody, fontSize: "0.8rem", fontWeight: 700,
                letterSpacing: 2, textTransform: "uppercase", marginBottom: "1.5rem",
              }}>{item.label}</div>
              <div style={{ width: 1, height: 30, background: "rgba(247,245,235,0.15)", margin: "0 auto 1.5rem" }} />
              <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>{item.icon}</div>
              {item.text ? (
                <p style={{ fontFamily: fontBody, fontSize: "0.88rem", color: ALABASTER, lineHeight: 1.75, opacity: 0.85, fontWeight: 500 }}>{item.text}</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", alignItems: "center" }}>
                  {tx.values.map((v, vi) => (
                    <span key={vi} style={{ fontFamily: fontBody, fontSize: "0.85rem", color: ALABASTER, opacity: 0.85, fontWeight: 500 }}>{v}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── PERKS ── */}
      <section style={{ background: ALABASTER, padding: "5rem 8%", direction: isRTL ? "rtl" : "ltr" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: "4rem", marginBottom: "4rem", alignItems: "center" }} className="perks-top-grid">
          <div style={{ textAlign: isRTL ? "right" : "left" }}>
            <p style={{ fontFamily: fontBody, fontSize: "0.72rem", letterSpacing: 3, textTransform: "uppercase", color: HIGHLIGHT_BLUE, marginBottom: "0.8rem", fontWeight: 700 }}>{tx.perksLabel}</p>
            <h2 style={{ fontFamily: fontHeading, fontSize: "clamp(1.9rem,3vw,2.7rem)", color: RICH_BLUE, lineHeight: 1.2, fontWeight: 700, marginBottom: "1.5rem" }}>{tx.perksTitle}</h2>
            <p style={{ fontFamily: fontBody, fontSize: "0.92rem", color: RICH_BLUE, lineHeight: 1.85, opacity: 0.75, fontWeight: 500, maxWidth: 540 }}>
              {lang === "en"
                ? "Every bean we source goes through rigorous selection to ensure nothing but the finest espresso experience, every single time. From farm to cup, quality is our promise."
                : "كل حبة نختارها تمر بعملية انتقاء صارمة لضمان تجربة الإسبريسو الأفضل في كل مرة. من المزرعة إلى الكوب، الجودة هي وعدنا."}
            </p>
          </div>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <img src={PerksCupImg} alt="Cup of Coffee Beans and Scoop" className="perks-cup-image" />
          </div>
        </div>
        <div className="perks-bottom-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "2rem" }}>
          {[
            { title: tx.perk1T, text: tx.perk1P, icon: "⏱" },
            { title: tx.perk2T, text: tx.perk2P, icon: "⭐" },
            { title: tx.perk3T, text: tx.perk3P, icon: "✓"  },
          ].map((p, i) => (
            <div key={i} className="perk-card" style={{ background: "#fff", border: `1px solid var(--gold-border)`, borderRadius: "12px", padding: "2.5rem 2rem", boxShadow: "0 4px 12px rgba(0,0,0,0.02)" }}>
              <div style={{ width: 44, height: 44, border: `1.5px solid rgba(197, 160, 112, 0.3)`, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.5rem", fontSize: "1.2rem", color: HIGHLIGHT_BLUE, background: "rgba(197, 160, 112, 0.05)" }}>{p.icon}</div>
              <h3 style={{ fontFamily: fontBody, fontSize: "1.2rem", color: RICH_BLUE, marginBottom: "0.7rem", fontWeight: 700 }}>{p.title}</h3>
              <p style={{ fontFamily: fontBody, fontSize: "0.85rem", color: RICH_BLUE, lineHeight: 1.75, opacity: 0.7, fontWeight: 500 }}>{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CRAFTED EXCELLENCE BANNER ── */}
      <section className="responsive-grid-split" style={{ background: RICH_BLUE, direction: isRTL ? "rtl" : "ltr" }}>
        <div style={{ padding: "5rem 8%", display: "flex", flexDirection: "column", justifyContent: "center", textAlign: isRTL ? "right" : "left" }}>
          <h2 style={{ fontFamily: fontHeading, fontSize: "clamp(1.9rem,3vw,2.7rem)", color: ALABASTER, lineHeight: 1.2, marginBottom: "1.5rem", fontWeight: 700 }}>
            {lang === "en" 
              ? '"Crafted Excellence in Every Roast: Freshly Brewed Perfection!"'
              : '"تميز مصنع في كل تحميص: كمال محضر طازجًا!"'}
          </h2>
          <p style={{ fontFamily: fontBody, fontSize: "0.92rem", color: ALABASTER, opacity: 0.8, lineHeight: 1.85, marginBottom: "2.5rem", fontWeight: 500, maxWidth: 500 }}>
            {lang === "en"
              ? "Our master roasters carefully craft each batch, unlocking the rich, complex flavors of authentic Neapolitan espresso. Experience the passion in every sip."
              : "يصنع محمّصونا الرئيسيون كل دفعة بعناية، ليطلقوا النكهات الغنية والمعقدة للإسبريسو النابوليتاني الأصيل. اختبر الشغف في كل رشفة."}
          </p>
          <button className="btn-hover" style={{
            background: HIGHLIGHT_BLUE, color: "#fff", border: "none",
            padding: "0.85rem 2.2rem", borderRadius: "30px",
            fontFamily: fontBody, fontSize: "0.85rem", fontWeight: 700,
            cursor: "pointer", alignSelf: isRTL ? "flex-end" : "flex-start", letterSpacing: 1, textTransform: "uppercase",
            boxShadow: "0 4px 10px rgba(197, 160, 112, 0.2)"
          }}>{tx.learnMore}</button>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "4rem 5%", minHeight: 480 }}>
          <div className="banner-image-wrapper">
            <img src={MiddleHandsImg} alt="Crafted Coffee Roasting Hands" className="banner-hands-image" />
          </div>
        </div>
      </section>

      {/* ── WHY IL CAPO ── */}
      <section className="responsive-grid-split" style={{ background: ALABASTER, direction: isRTL ? "rtl" : "ltr" }}>
        <div style={{ background: RICH_BLUE, display: "flex", alignItems: "center", justifyContent: "center", padding: "4rem 2rem", minHeight: 500, position: "relative" }}>
          <IlCapoLogo size={280} variant="alabaster-on-blue" />
          <div style={{
            position: "absolute",
            bottom: "2.5rem",
            left: "50%",
            transform: "translateX(-50%)",
            background: ALABASTER,
            color: RICH_BLUE,
            padding: "0.7rem 1.6rem",
            borderRadius: "30px",
            fontFamily: fontBody,
            fontSize: "0.72rem",
            letterSpacing: lang === "ar" ? 2 : 2.5,
            textTransform: "uppercase",
            fontWeight: 700,
            whiteSpace: "nowrap",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
          }}>
            {lang === "ar" ? "تأسس ٢٠١٢" : "Est. 2012"}
          </div>
        </div>
        <div style={{ padding: "5rem 8%", display: "flex", flexDirection: "column", justifyContent: "center", textAlign: isRTL ? "right" : "left" }}>
          <p style={{ fontFamily: fontBody, fontSize: "0.72rem", letterSpacing: 3, textTransform: "uppercase", color: HIGHLIGHT_BLUE, marginBottom: "0.8rem", fontWeight: 700 }}>{tx.whyLabel}</p>
          <h2 style={{ fontFamily: fontHeading, fontSize: "clamp(1.9rem,3vw,2.7rem)", color: RICH_BLUE, lineHeight: 1.2, marginBottom: "1rem", fontWeight: 700 }}>{tx.whyTitle}</h2>
          <p style={{ fontFamily: fontBody, fontSize: "0.92rem", color: RICH_BLUE, lineHeight: 1.85, marginBottom: "2.5rem", opacity: 0.75, fontWeight: 500 }}>{tx.whyP}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            {[
              { num: "01", title: tx.w1T, text: tx.w1P },
              { num: "02", title: tx.w2T, text: tx.w2P },
              { num: "03", title: tx.w3T, text: tx.w3P },
              { num: "04", title: tx.w4T, text: tx.w4P },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: "1.2rem", alignItems: "flex-start", paddingBottom: "1.5rem", borderBottom: i < 3 ? `1px solid rgba(33,48,69,0.06)` : "none", flexDirection: isRTL ? "row-reverse" : "row" }}>
                <span style={{ fontFamily: fontBody, fontSize: "1.6rem", color: "rgba(33,48,69,0.15)", fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>{item.num}</span>
                <div style={{ textAlign: isRTL ? "right" : "left" }}>
                  <h4 style={{ fontFamily: fontBody, fontSize: "0.85rem", letterSpacing: 1, textTransform: "uppercase", color: RICH_BLUE, marginBottom: "0.3rem", fontWeight: 700 }}>{item.title}</h4>
                  <p style={{ fontFamily: fontBody, fontSize: "0.85rem", color: RICH_BLUE, lineHeight: 1.7, opacity: 0.7, fontWeight: 500 }}>{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRODUCTS ── */}
      <section style={{ padding: "6rem 8%", background: RICH_BLUE }}>
        <div style={{ textAlign: "center", marginBottom: "4rem" }}>
          <p style={{ fontFamily: fontBody, fontSize: "0.72rem", letterSpacing: 3, textTransform: "uppercase", color: LIGHT_BLUE, marginBottom: "0.8rem", fontWeight: 700 }}>{tx.productsLabel}</p>
          <h2 style={{ fontFamily: fontHeading, fontSize: "clamp(1.9rem,3vw,2.7rem)", color: ALABASTER, fontWeight: 700 }}>{tx.productsTitle}</h2>
        </div>
        <div className="responsive-grid-4">
          {tx.products.map((p, i) => {
            const productImages = [
              ProductClassicoImg, // Classico
              ProductIntensoImg,   // Intenso
              ProductDolceImg,     // Dolce
              ProductOrigineImg,   // Origine
            ];
            return (
              <div key={i} className="product-card" style={{ background: "rgba(247,245,235,0.04)", border: `1px solid rgba(247,245,235,0.08)`, borderRadius: 8, overflow: "hidden" }}>
                <div style={{ aspectRatio: "3/4", background: `rgba(247,245,235,0.02)`, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", overflow: "hidden" }}>
                  <img src={productImages[i]} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "4px" }} />
                </div>
                <div style={{ padding: "1.4rem" }}>
                  <p style={{ fontFamily: fontBody, fontSize: "0.68rem", letterSpacing: 2, textTransform: "uppercase", color: LIGHT_BLUE, marginBottom: "0.3rem", fontWeight: 700 }}>{p.origin}</p>
                  <h3 style={{ fontFamily: fontBody, fontSize: "1.2rem", color: ALABASTER, marginBottom: "0.3rem", fontWeight: 700 }}>{p.name}</h3>
                  <p style={{ fontFamily: fontBody, fontSize: "0.8rem", color: ALABASTER, opacity: 0.65, marginBottom: "1.2rem", fontStyle: "italic", fontWeight: 500 }}>{p.desc}</p>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "1rem" }}>
                    <button className="btn-hover" style={{ background: "transparent", border: `1px solid rgba(247,245,235,0.25)`, color: ALABASTER, padding: "0.6rem 2rem", borderRadius: "30px", fontFamily: fontBody, fontSize: "0.8rem", letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", fontWeight: 700, width: "100%" }}>{tx.orderNow}</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── TESTIMONIAL ── */}
      <section style={{ background: ALABASTER, padding: "6rem 8%", textAlign: "center", borderTop: `1px solid rgba(33,48,69,0.06)` }}>
        <p style={{ fontFamily: fontBody, fontSize: "0.72rem", letterSpacing: 3, textTransform: "uppercase", color: HIGHLIGHT_BLUE, marginBottom: "0.8rem", fontWeight: 700 }}>{tx.testLabel}</p>
        <h2 style={{ fontFamily: fontHeading, fontSize: "clamp(1.6rem,3vw,2.4rem)", color: RICH_BLUE, marginBottom: "3rem", fontWeight: 700 }}>{tx.testTitle}</h2>
        <p style={{ fontFamily: fontBody, fontSize: "clamp(1.1rem,2vw,1.35rem)", color: RICH_BLUE, fontStyle: "italic", maxWidth: 720, margin: "0 auto 2.5rem", lineHeight: 1.8, opacity: 0.85, fontWeight: 500 }}>
          "{tx.testQuote}"
        </p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "1rem" }}>
          <div style={{ width: 50, height: 50, borderRadius: "50%", background: RICH_BLUE, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: fontBody, fontSize: "1.1rem", color: ALABASTER, fontWeight: 700 }}>
            {tx.testName.split(" ").map(n => n[0]).join("")}
          </div>
          <div style={{ textAlign: isRTL ? "right" : "left" }}>
            <p style={{ fontFamily: fontBody, fontSize: "0.92rem", color: RICH_BLUE, fontWeight: 700 }}>{tx.testName}</p>
            <p style={{ fontFamily: fontBody, fontSize: "0.78rem", color: RICH_BLUE, opacity: 0.65, fontWeight: 500 }}>{tx.testRole}</p>
          </div>
        </div>
      </section>

      {/* ── BLOG ── */}
      <section style={{ padding: "6rem 8%", background: ALABASTER }}>
        <div style={{ textAlign: "center", marginBottom: "4rem" }}>
          <p style={{ fontFamily: fontBody, fontSize: "0.72rem", letterSpacing: 3, textTransform: "uppercase", color: HIGHLIGHT_BLUE, marginBottom: "0.8rem", fontWeight: 700 }}>{tx.blogLabel}</p>
          <h2 style={{ fontFamily: fontHeading, fontSize: "clamp(1.6rem,3vw,2.4rem)", color: RICH_BLUE, fontWeight: 700 }}>{tx.blogTitle}</h2>
        </div>
        <div className="responsive-grid-3">
          {tx.blogs.map((b, i) => (
            <div key={i} style={{ background: "#fff", border: `1px solid rgba(33,48,69,0.06)`, borderRadius: 8, overflow: "hidden", boxShadow: "0 4px 15px rgba(33,48,69,0.03)" }}>
              <div style={{ aspectRatio: "3/2", background: `rgba(33,48,69,${0.04 + i * 0.03})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: "2.5rem", opacity: 0.4 }}>{["☕", "🔥", "🌱"][i]}</span>
              </div>
              <div style={{ padding: "1.5rem" }}>
                <p style={{ fontFamily: fontBody, fontSize: "0.68rem", letterSpacing: 2, textTransform: "uppercase", color: HIGHLIGHT_BLUE, marginBottom: "0.5rem", fontWeight: 700 }}>{b.date}</p>
                <h3 style={{ fontFamily: fontBody, fontSize: "1.15rem", color: RICH_BLUE, lineHeight: 1.4, marginBottom: "0.7rem", fontWeight: 700 }}>{b.title}</h3>
                <p style={{ fontFamily: fontBody, fontSize: "0.82rem", color: RICH_BLUE, opacity: 0.7, lineHeight: 1.7, fontWeight: 500 }}>{b.excerpt}</p>
                <a href="#/" style={{ display: "inline-block", marginTop: "1.2rem", fontFamily: fontBody, fontSize: "0.75rem", letterSpacing: 1.5, textTransform: "uppercase", color: HIGHLIGHT_BLUE, textDecoration: "none", borderBottom: `1px solid rgba(63,88,114,0.3)`, paddingBottom: 2, fontWeight: 700 }}>{tx.readMore}</a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── NEWSLETTER ── */}
      <section style={{ padding: "5rem 8%", background: RICH_BLUE, textAlign: "center" }}>
        <p style={{ fontFamily: fontBody, fontSize: "0.72rem", letterSpacing: 3, textTransform: "uppercase", color: LIGHT_BLUE, marginBottom: "0.8rem", fontWeight: 700 }}>{tx.newsletterTag}</p>
        <h2 style={{ fontFamily: fontHeading, fontSize: "clamp(1.3rem,2vw,2rem)", color: ALABASTER, marginBottom: "2rem", maxWidth: 520, marginLeft: "auto", marginRight: "auto", fontStyle: "italic", fontWeight: 600 }}>{tx.newsletterTitle}</h2>
        <div style={{ display: "flex", maxWidth: 460, margin: "0 auto" }}>
          <input
            type="email"
            placeholder={tx.emailPlaceholder}
            style={{
              flex: 1,
              background: "rgba(247,245,235,0.07)",
              border: `1px solid rgba(247,245,235,0.15)`,
              borderInlineEnd: "none",
              color: ALABASTER,
              padding: "0.85rem 1.2rem",
              fontFamily: fontBody,
              fontSize: "0.85rem",
              outline: "none",
              borderStartStartRadius: "30px",
              borderEndStartRadius: "30px",
              borderStartEndRadius: "0",
              borderEndEndRadius: "0",
            }}
          />
          <button className="btn-hover" style={{
            background: ALABASTER,
            color: RICH_BLUE,
            border: "none",
            padding: "0.85rem 1.8rem",
            fontFamily: fontBody,
            fontSize: "0.78rem",
            letterSpacing: 1.5,
            textTransform: "uppercase",
            fontWeight: 700,
            cursor: "pointer",
            borderStartEndRadius: "30px",
            borderEndEndRadius: "30px",
            borderStartStartRadius: "0",
            borderEndStartRadius: "0",
          }}>{tx.subscribe}</button>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: "#19110E", padding: "5rem 8% 2.5rem", borderTop: `1px solid rgba(247,245,235,0.06)` }}>
        <div className="responsive-grid-footer">
          <div>
            <div style={{ marginBottom: "1.5rem" }}>
              <IlCapoLogo size={280} variant="alabaster-on-blue" />
            </div>
            <p style={{ fontFamily: fontBody, fontSize: "0.85rem", color: "rgba(247,245,235,0.55)", lineHeight: 1.75, fontWeight: 500 }}>{tx.footerDesc}</p>
          </div>
          {[
            { title: tx.footerAbout,   links: tx.footerAboutLinks   },
            { title: tx.footerExplore, links: tx.footerExploreLinks  },
            { title: tx.footerInquiries, links: ["📞 +218 91 234 5678", "✉ info@ilcapo.ly", "📍 Tripoli, Libya", "🕐 " + (lang === "en" ? "Mon–Sat: 9am–9pm" : "الإثنين–السبت: ٩ص–٩م")] },
          ].map((col, i) => (
            <div key={i}>
              <h5 style={{ fontFamily: fontBody, fontSize: "0.72rem", letterSpacing: 2, textTransform: "uppercase", color: "rgba(247,245,235,0.65)", marginBottom: "1.2rem", fontWeight: 700 }}>{col.title}</h5>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {col.links.map((l) => (
                  <li key={l} style={{ fontFamily: fontBody, fontSize: "0.85rem", color: "rgba(247,245,235,0.45)", cursor: "pointer", fontWeight: 500, transition: "color 0.2s" }} onMouseEnter={(e) => e.target.style.color = ALABASTER} onMouseLeave={(e) => e.target.style.color = "rgba(247,245,235,0.45)"}>{l}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "2.5rem", borderTop: `1px solid rgba(247,245,235,0.06)` }}>
          <p style={{ fontFamily: fontBody, fontSize: "0.8rem", color: "rgba(247,245,235,0.4)", fontWeight: 500 }}>{tx.footerCopy}</p>
          <div style={{ display: "flex", gap: "0.8rem" }}>
            {["in", "📷", "𝕏"].map((s, i) => (
              <div key={i} style={{ width: 34, height: 34, borderRadius: "50%", border: `1px solid rgba(247,245,235,0.15)`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "0.8rem", color: "rgba(247,245,235,0.45)", transition: "all 0.2s" }} onMouseEnter={(e) => { e.target.style.color = ALABASTER; e.target.style.borderColor = ALABASTER; }} onMouseLeave={(e) => { e.target.style.color = "rgba(247,245,235,0.45)"; e.target.style.borderColor = "rgba(247,245,235,0.15)"; }}>{s}</div>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <IlCapoWebsite />
  </React.StrictMode>
);
